// src/assets/naming.ts
var ASSET_FORMATS = ["svg", "webp", "png", "jpg", "gif", "avif"];
var DEFAULT_ASSET_FORMAT = "svg";
var ASSET_MIME = {
  svg: "image/svg+xml",
  webp: "image/webp",
  png: "image/png",
  jpg: "image/jpeg",
  gif: "image/gif",
  avif: "image/avif"
};
var ASSET_ROOT = "logos";
var CDN_ASSET_ROOT = "public/GET/cdn";
var PRIVATE_ASSET_ROOT = "assets/private";
var ALLOWED_ASSET_ROOTS = [ASSET_ROOT, CDN_ASSET_ROOT, PRIVATE_ASSET_ROOT];
var AssetNamingError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "AssetNamingError";
  }
};
var SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
var ADDRESS_RE = /^0x[0-9a-f]{40}$/;
var MAX_SEGMENT = 80;
function isAssetFormat(value) {
  return typeof value === "string" && ASSET_FORMATS.includes(value);
}
function mimeForAssetFormat(format) {
  return ASSET_MIME[format];
}
function assetFormatFromExtension(pathOrExt) {
  const ext = pathOrExt.split(".").pop()?.toLowerCase().replace("jpeg", "jpg");
  return isAssetFormat(ext) ? ext : void 0;
}
function isValidAssetSlug(slug) {
  return SLUG_RE.test(slug) && slug.length <= MAX_SEGMENT;
}
function slugifyAsset(input) {
  const slug = input.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, MAX_SEGMENT).replace(/-+$/g, "");
  if (!slug) throw new AssetNamingError(`Cannot derive an asset slug from "${input}"`);
  return slug;
}
function fmt(format) {
  if (format && !isAssetFormat(format)) {
    throw new AssetNamingError(`Unsupported asset format "${format}" (allowed: ${ASSET_FORMATS.join(", ")})`);
  }
  return format ?? DEFAULT_ASSET_FORMAT;
}
function normalizeAddressForKey(address) {
  const lower = address.trim().toLowerCase();
  if (!ADDRESS_RE.test(lower)) {
    throw new AssetNamingError(`Invalid token address "${address}" (expected 0x + 40 hex)`);
  }
  return lower;
}
function chainIdToDecimal(chainId) {
  const v = chainId.trim().toLowerCase();
  const reference = v.includes(":") ? v.slice(v.indexOf(":") + 1) : v;
  if (/^0x[0-9a-f]+$/.test(reference)) return BigInt(reference).toString(10);
  if (/^\d+$/.test(reference)) return reference;
  return null;
}
function chainLogoKey(input) {
  const ext = fmt(input.format);
  const variant = input.variant ? `.${slugifyAsset(input.variant)}` : "";
  if (input.slug) {
    const slug = isValidAssetSlug(input.slug) ? input.slug : slugifyAsset(input.slug);
    return `${ASSET_ROOT}/chains/${slug}${variant}.${ext}`;
  }
  if (input.chainId) {
    const decimal = chainIdToDecimal(input.chainId);
    if (!decimal) throw new AssetNamingError(`Unusable chain id "${input.chainId}"`);
    return `${ASSET_ROOT}/chains/by-id/${decimal}${variant}.${ext}`;
  }
  throw new AssetNamingError("chainLogoKey requires a `slug` or `chainId`");
}
function tokenLogoKey(input) {
  const chain = isValidAssetSlug(input.chain) ? input.chain : slugifyAsset(input.chain);
  const address = normalizeAddressForKey(input.address);
  return `${ASSET_ROOT}/tokens/${chain}/${address}.${fmt(input.format)}`;
}
function entityLogoKey(input) {
  const id = isValidAssetSlug(input.entityId) ? input.entityId : slugifyAsset(input.entityId);
  return `${ASSET_ROOT}/entities/${id}.${fmt(input.format)}`;
}
function tagLogoKey(input) {
  const id = isValidAssetSlug(input.tagId) ? input.tagId : slugifyAsset(input.tagId);
  return `${ASSET_ROOT}/tags/${id}.${fmt(input.format)}`;
}
function labelLogoKey(input) {
  const id = isValidAssetSlug(input.labelId) ? input.labelId : slugifyAsset(input.labelId);
  return `${ASSET_ROOT}/labels/${id}.${fmt(input.format)}`;
}
function groupLogoKey(input) {
  const id = isValidAssetSlug(input.groupId) ? input.groupId : slugifyAsset(input.groupId);
  return `${ASSET_ROOT}/groups/${id}.${fmt(input.format)}`;
}
function addressLogoKey(input) {
  const chain = isValidAssetSlug(input.chain) ? input.chain : slugifyAsset(input.chain);
  const address = normalizeAddressForKey(input.address);
  return `${ASSET_ROOT}/addresses/${chain}/${address}.${fmt(input.format)}`;
}
function catalogLogoKey(input) {
  switch (input.kind) {
    case "entity":
      return entityLogoKey({ entityId: input.id, format: input.format });
    case "tag":
      return tagLogoKey({ tagId: input.id, format: input.format });
    case "label":
      return labelLogoKey({ labelId: input.id, format: input.format });
    case "group":
      return groupLogoKey({ groupId: input.id, format: input.format });
    case "address":
      if (!input.chain) throw new AssetNamingError("address logo key requires a `chain`");
      return addressLogoKey({ chain: input.chain, address: input.id, format: input.format });
  }
}
function tenantLogoKey(input) {
  const id = isValidAssetSlug(input.tenantId) ? input.tenantId : slugifyAsset(input.tenantId);
  const variant = input.variant ? `.${input.variant}` : "";
  return `${ASSET_ROOT}/tenants/${id}${variant}.${fmt(input.format)}`;
}
function assertValidAssetKey(key) {
  if (!key || key.startsWith("/") || key.includes("\\") || key.includes("..") || key.includes("//")) {
    throw new AssetNamingError(`Invalid asset key "${key}"`);
  }
  if (!ALLOWED_ASSET_ROOTS.some((root) => key.startsWith(`${root}/`))) {
    const roots = ALLOWED_ASSET_ROOTS.map((r) => `"${r}/"`).join(" or ");
    throw new AssetNamingError(`Asset key "${key}" must start with ${roots}`);
  }
  if (!assetFormatFromExtension(key)) {
    throw new AssetNamingError(`Asset key "${key}" must end in one of: ${ASSET_FORMATS.join(", ")}`);
  }
  if (new TextEncoder().encode(key).length > 1024) {
    throw new AssetNamingError("Asset key exceeds S3's 1024-byte limit");
  }
  if (key.startsWith(`${ASSET_ROOT}/`)) {
    for (const segment of key.split("/")) {
      if (segment.length > MAX_SEGMENT) {
        throw new AssetNamingError(`Asset key segment "${segment}" exceeds ${MAX_SEGMENT} chars`);
      }
    }
  }
}
function assetUrl(key, opts) {
  assertValidAssetKey(key);
  const base = opts.cdnBase.replace(/\/+$/, "");
  return `${base}/${key.split("/").map(encodeURIComponent).join("/")}`;
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  /** Hyperliquid's L1 (HyperCore): one signed action per transaction, EVM-shaped identifiers. */
  HYPERCORE: "hypercore",
  OTHER: "other"
};

// src/chains/seed-data.ts
var CHAIN_MAPPING = {
  // --- EVM mainnets ---
  "0x1": "eth",
  "0x3d": "etc",
  "0x89": "polygon",
  "0x38": "bsc",
  "0xcc": "opbnb",
  "0xa86a": "avalanche",
  "0xfa": "fantom",
  "0x19": "cronos",
  "0x2a15c308d": "palm",
  "0xa4b1": "arbitrum",
  "0x64": "gnosis",
  "0x15b38": "chiliz",
  "0x2105": "base",
  "0xa": "optimism",
  "0x2019": "klaytn",
  "0xa4ec": "celo",
  "0x8ae": "kava",
  "0x440": "metis",
  "0x63564c40": "harmony",
  "0x171": "pulsechain",
  "0x1388": "mantle",
  // Nesa. A Cosmos-SDK chain with an EVM module, so its Cosmos chain id
  // (`nesa_41443-1`) and its EVM chain id are the same 41443. Ported from
  // `_seed_data.py`, which has carried it since the Hyperlane domain fallback
  // was retired — without this row every spelling of Nesa falls through the
  // registry unresolved and renders as a raw `eip155:41443`.
  "0xa1e3": "nesa",
  "0x505": "moonriver",
  "0x504": "moonbeam",
  "0x144": "zkSync_era",
  "0x44d": "polygon_zkevm",
  "0xe708": "linea",
  "0x82750": "scroll",
  "0x13e31": "blast",
  "0x4e454152": "aurora",
  "0x7a": "fuse",
  "0x2eb": "flow",
  "0x7e4": "ronin",
  "0x46f": "lisk",
  "0x1b58": "zetachain",
  "0x531": "sei",
  // HyperEVM. Its L1 order book (HyperCore) is a non-EVM chain in registry.ts.
  "0x3e7": "hyperevm",
  // Ink, Kraken's OP Stack L2 — ported from `_seed_data.py` (CCIP + LayerZero route to it).
  "0xdef1": "ink",
  "0x8f": "monad",
  "0x2611": "plasma",
  // peaq. Ported from `_seed_data.py`, where it was seeded because a LayerZero
  // OFT leg named its endpoint id and the chip rendered the bare number. The
  // lower-case name is the project's own styling.
  "0xd0a": "peaq",
  // GRVT, a ZK Stack chain. This id was seeded as `zkSync_era_testnet`, so
  // `eip155:325` resolved as a zkSync Era testnet paired with Era mainnet; Era's
  // testnets are 280 and 300. Ported from `_seed_data.py`, which carries the
  // sources for the id and for the native token (GBT, not ETH).
  "0x145": "grvt",
  // --- EVM testnets ---
  "0x5": "goerli",
  "0xaa36a7": "sepolia",
  "0x4268": "holesky",
  "0x13881": "mumbai",
  "0x13882": "polygon_amoy",
  "0x61": "bsc_testnet",
  "0x27d8": "gnosis_chiado",
  "0x15b32": "chiliz_spicy",
  "0x14a33": "base_goerli",
  "0x14a34": "base_sepolia",
  "0x1a4": "optimism_goerli",
  "0xaa37dc": "optimism_sepolia",
  "0x66eee": "arbitrum_sepolia",
  "0x3e9": "klaytn_baobab",
  "0xaef3": "celo_alfajores",
  "0x8ad": "kava_testnet",
  "0x24c": "metis_goerli",
  "0x6357d2e0": "harmony_testnet",
  "0x4e454153": "aurora_testnet",
  "0x507": "moonbase",
  "0x118": "zkSync_testnet",
  "0x12c": "zksync_sepolia",
  "0x7b": "fuse_testnet",
  "0xe704": "linea_testnet",
  "0xe705": "linea_sepolia",
  "0x8274f": "scroll_testnet",
  "0x138b": "mantle_sepolia",
  "0xa0c71fd": "blast_sepolia",
  "0x98a": "polygon_zkevm_cardona",
  "0xfa2": "fantom_testnet",
  "0x106a": "lisk_sepolia",
  "0x221": "flow_testnet",
  "0x7e5": "ronin_saigon",
  "0x1b59": "zetachain_testnet",
  "0x530": "sei_testnet",
  "0x3e6": "hyperevm_testnet"
};
var TESTNET_CHAINS = [
  "goerli",
  "sepolia",
  "mumbai",
  "bsc_testnet",
  "gnosis_chiado",
  "chiliz_spicy",
  "base_goerli",
  "optimism_goerli",
  "klaytn_baobab",
  "celo_alfajores",
  "kava_testnet",
  "metis_goerli",
  "harmony_testnet",
  "zkSync_testnet",
  "aurora_testnet",
  "moonbase",
  "fuse_testnet",
  "linea_testnet",
  "scroll_testnet",
  "holesky",
  "polygon_amoy",
  "base_sepolia",
  "optimism_sepolia",
  "arbitrum_sepolia",
  "linea_sepolia",
  "blast_sepolia",
  "mantle_sepolia",
  "zksync_sepolia",
  "polygon_zkevm_cardona",
  "fantom_testnet",
  "lisk_sepolia",
  "flow_testnet",
  "ronin_saigon",
  "zetachain_testnet",
  "sei_testnet",
  "hyperevm_testnet"
];
var LAYER1_CHAINS = [
  "eth",
  "etc",
  "bsc",
  "avalanche",
  "fantom",
  "klaytn",
  "celo",
  "kava",
  "harmony",
  "moonriver",
  "moonbeam",
  "fuse",
  "pulsechain",
  "flow",
  "ronin",
  "sei",
  "monad",
  "peaq",
  "zetachain",
  "hyperevm"
];
var LAYER2_CHAINS = [
  "polygon",
  "arbitrum",
  "optimism",
  "metis",
  "base",
  "zkSync_era",
  "linea",
  "scroll",
  "mantle",
  "opbnb",
  "polygon_zkevm",
  "blast",
  "lisk",
  "grvt",
  "ink"
];
var SIDECHAINS = ["palm", "gnosis", "cronos", "aurora"];
var CHAIN_CONFIG = {
  "0x1": { token: "ETH", gasUnit: "Gwei", name: "Ethereum" },
  "0x3d": { token: "ETC", gasUnit: "Gwei", name: "Ethereum Classic" },
  "0x89": { token: "MATIC", gasUnit: "Gwei", name: "Polygon" },
  "0x38": { token: "BNB", gasUnit: "Gwei", name: "BNB Chain" },
  // Gas on Arbitrum is ETH; ARB is a governance token.
  "0xa4b1": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum" },
  "0x2105": { token: "ETH", gasUnit: "Gwei", name: "Base" },
  "0xa": { token: "ETH", gasUnit: "Gwei", name: "Optimism" },
  "0xe708": { token: "ETH", gasUnit: "Gwei", name: "Linea" },
  "0xa86a": { token: "AVAX", gasUnit: "nAVAX", name: "Avalanche" },
  "0xfa": { token: "FTM", gasUnit: "Gwei", name: "Fantom" },
  "0x19": { token: "CRO", gasUnit: "Gwei", name: "Cronos" },
  "0x64": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis" },
  "0x15b38": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz" },
  "0x171": { token: "PLS", gasUnit: "Gwei", name: "PulseChain" },
  "0x504": { token: "GLMR", gasUnit: "Gwei", name: "Moonbeam" },
  "0x505": { token: "MOVR", gasUnit: "Gwei", name: "Moonriver" },
  "0x13e31": { token: "ETH", gasUnit: "Gwei", name: "Blast" },
  "0x144": { token: "ETH", gasUnit: "Gwei", name: "zkSync Era" },
  "0x1388": { token: "MNT", gasUnit: "Gwei", name: "Mantle" },
  "0xa1e3": { token: "NES", gasUnit: "Gwei", name: "Nesa" },
  "0xd0a": { token: "PEAQ", gasUnit: "Gwei", name: "peaq" },
  // GRVT pays gas in GBT (its ZKsync Bridgehub base token), not ETH.
  "0x145": { token: "GBT", gasUnit: "Gwei", name: "GRVT" },
  "0xcc": { token: "BNB", gasUnit: "Gwei", name: "opBNB" },
  // Gas on Polygon zkEVM is ETH, not MATIC.
  "0x44d": { token: "ETH", gasUnit: "Gwei", name: "Polygon zkEVM" },
  "0x1b58": { token: "ZETA", gasUnit: "Gwei", name: "ZetaChain" },
  "0x82750": { token: "ETH", gasUnit: "Gwei", name: "Scroll" },
  "0x4e454152": { token: "ETH", gasUnit: "Gwei", name: "Aurora" },
  "0x531": { token: "SEI", gasUnit: "Gwei", name: "Sei" },
  "0x3e7": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM" },
  "0xdef1": { token: "ETH", gasUnit: "Gwei", name: "Ink" },
  "0x3e6": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM Testnet" },
  "0x8f": { token: "MON", gasUnit: "Gwei", name: "Monad" },
  "0x3e9": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn Baobab" },
  "0x4e454153": { token: "ETH", gasUnit: "Gwei", name: "Aurora Testnet" },
  "0x8274f": { token: "ETH", gasUnit: "Gwei", name: "Scroll Sepolia" },
  "0x530": { token: "SEI", gasUnit: "Gwei", name: "Sei Testnet" },
  "0x2eb": { token: "FLOW", gasUnit: "FLOW units", name: "Flow" },
  "0x7e4": { token: "RON", gasUnit: "Gwei", name: "Ronin" },
  "0x46f": { token: "LSK", gasUnit: "LSK units", name: "Lisk" },
  "0xaa36a7": { token: "ETH", gasUnit: "Gwei", name: "Sepolia" },
  "0x4268": { token: "ETH", gasUnit: "Gwei", name: "Holesky" },
  "0x13882": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Amoy" },
  "0x61": { token: "tBNB", gasUnit: "Gwei", name: "BSC Testnet" },
  "0x66eee": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum Sepolia" },
  "0x14a34": { token: "ETH", gasUnit: "Gwei", name: "Base Sepolia" },
  "0xaa37dc": { token: "ETH", gasUnit: "Gwei", name: "Optimism Sepolia" },
  "0xe705": { token: "ETH", gasUnit: "Gwei", name: "Linea Sepolia" },
  "0xfa2": { token: "FTM", gasUnit: "Gwei", name: "Fantom Testnet" },
  "0x27d8": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis Chiado" },
  "0x15b32": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz Testnet" },
  "0x507": { token: "DEV", gasUnit: "Gwei", name: "Moonbase" },
  "0xa0c71fd": { token: "ETH", gasUnit: "Gwei", name: "Blast Sepolia" },
  "0x12c": { token: "ETH", gasUnit: "Gwei", name: "zkSync Sepolia" },
  "0x138b": { token: "MNT", gasUnit: "Gwei", name: "Mantle Sepolia" },
  "0x98a": { token: "MATIC", gasUnit: "Gwei", name: "Polygon zkEVM Cardona" },
  "0x1b59": { token: "ZETA", gasUnit: "Gwei", name: "Zetachain Testnet" },
  "0x221": { token: "FLOW", gasUnit: "FLOW units", name: "Flow Testnet" },
  "0x7e5": { token: "RON", gasUnit: "Gwei", name: "Ronin Saigon Testnet" },
  "0x106a": { token: "LSK", gasUnit: "LSK units", name: "Lisk Sepolia" },
  "0x0": { token: "BTC", gasUnit: "Sats/byte", name: "Bitcoin" },
  "0x1399c": { token: "SOL", gasUnit: "Lamports", name: "Solana" },
  "0x162": { token: "DOT", gasUnit: "Plancks", name: "Polkadot" },
  "0x2611": { token: "XPL", gasUnit: "Gwei", name: "Plasma" },
  // Without a row, `seedFromCanonical` names a chain by its slug ("celo") and
  // gives it ETH. Every seeded chain has one; the rows below were added in
  // `_seed_data.py` first and ported, and the parity test keeps them in step.
  "0xa4ec": { token: "CELO", gasUnit: "Gwei", name: "Celo" },
  "0x440": { token: "METIS", gasUnit: "Gwei", name: "Metis" },
  "0x8ae": { token: "KAVA", gasUnit: "Gwei", name: "Kava" },
  "0x63564c40": { token: "ONE", gasUnit: "Gwei", name: "Harmony" },
  "0x7a": { token: "FUSE", gasUnit: "Gwei", name: "Fuse" },
  "0x2019": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn" },
  "0x2a15c308d": { token: "PALM", gasUnit: "Gwei", name: "Palm" },
  "0x5": { token: "ETH", gasUnit: "Gwei", name: "Goerli" },
  "0x13881": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Mumbai" },
  "0x14a33": { token: "ETH", gasUnit: "Gwei", name: "Base Goerli" },
  "0x1a4": { token: "ETH", gasUnit: "Gwei", name: "Optimism Goerli" },
  "0xaef3": { token: "CELO", gasUnit: "Gwei", name: "Celo Alfajores" },
  "0x8ad": { token: "TKAVA", gasUnit: "Gwei", name: "Kava Testnet" },
  "0x24c": { token: "METIS", gasUnit: "Gwei", name: "Metis Goerli" },
  "0x6357d2e0": { token: "ONE", gasUnit: "Gwei", name: "Harmony Testnet" },
  "0x118": { token: "ETH", gasUnit: "Gwei", name: "zkSync Goerli" },
  "0x7b": { token: "SPARK", gasUnit: "Gwei", name: "Fuse Sparknet" },
  "0xe704": { token: "ETH", gasUnit: "Gwei", name: "Linea Goerli" }
};
var EXPLORER_URLS = {
  eth: "https://etherscan.io",
  etc: "https://blockscout.com/etc/mainnet",
  polygon: "https://polygonscan.com",
  bsc: "https://bscscan.com",
  avalanche: "https://snowtrace.io",
  fantom: "https://ftmscan.com",
  arbitrum: "https://arbiscan.io",
  optimism: "https://optimistic.etherscan.io",
  zksync_era: "https://explorer.zksync.io",
  base: "https://basescan.org",
  linea: "https://lineascan.build",
  scroll: "https://scrollscan.com",
  mantle: "https://explorer.mantle.xyz",
  // The EVM-side Blockscout. Nesa also runs a Cosmos explorer at
  // `explorer.nesa.ai`, which cannot resolve an `0x` address or tx hash.
  nesa: "https://explorer-evm.nesa.ai",
  // Subscan, whose EVM mode answers the default /tx, /address, /block and
  // /token paths (each checked against a live peaq tx on 2026-09-16).
  peaq: "https://peaq.subscan.io",
  opbnb: "https://opbnbscan.com",
  polygon_zkevm: "https://zkevm.polygonscan.com",
  blast: "https://blastscan.io",
  pulsechain: "https://scan.pulsechain.com",
  aurora: "https://explorer.aurora.dev",
  flow: "https://evm.flowscan.io",
  ronin: "https://app.roninchain.com",
  lisk: "https://blockscout.lisk.com",
  zetachain: "https://explorer.zetachain.com",
  sei: "https://seitrace.com",
  hyperevm: "https://hyperevmscan.io",
  ink: "https://explorer.inkonchain.com",
  monad: "https://monadexplorer.com",
  plasma: "https://plasmascan.to",
  // --- Testnets ---
  // A testnet with no row here gets NO explorer link — the registry does not
  // lend it its mainnet's, which would show a different chain's object under
  // the same hash or address. So a row belongs here only when the testnet has
  // its own explorer that is live; these were checked on 2026-09-16 (DNS, the
  // site answering, and the chain id where the explorer exposes one).
  //
  // Deliberately absent, because the explorer is gone or now serves another
  // network: holesky (holesky.etherscan.io no longer resolves), mumbai
  // (mumbai.polygonscan.com no longer resolves — the Python table still carries
  // it), base_goerli, optimism_goerli, linea_testnet, zkSync_testnet,
  // metis_goerli, polygon_zkevm_cardona, fantom_testnet, celo_alfajores (both
  // its explorers redirect to Celo Sepolia), scroll_testnet
  // (sepolia.scrollscan.com is a Cloudflare 1014), lisk_sepolia (unreachable)
  // and sei_testnet (Seitrace picks the network by query string, which the link
  // builder cannot express).
  //
  // FE-only: the Python table has no mainnet fallback to correct, and feeds
  // scraping hosts, so these are not mirrored there.
  goerli: "https://goerli.etherscan.io",
  sepolia: "https://sepolia.etherscan.io",
  polygon_amoy: "https://amoy.polygonscan.com",
  bsc_testnet: "https://testnet.bscscan.com",
  gnosis_chiado: "https://gnosis-chiado.blockscout.com",
  base_sepolia: "https://sepolia.basescan.org",
  optimism_sepolia: "https://sepolia-optimism.etherscan.io",
  arbitrum_sepolia: "https://sepolia.arbiscan.io",
  aurora_testnet: "https://explorer.testnet.aurora.dev",
  moonbase: "https://moonbase.moonscan.io",
  fuse_testnet: "https://explorer.fusespark.io",
  linea_sepolia: "https://sepolia.lineascan.build",
  blast_sepolia: "https://sepolia.blastscan.io",
  mantle_sepolia: "https://sepolia.mantlescan.xyz",
  zksync_sepolia: "https://sepolia.explorer.zksync.io",
  flow_testnet: "https://testnet.evm.flow.com",
  ronin_saigon: "https://saigon-app.roninchain.com",
  zetachain_testnet: "https://testnet.zetascan.com"
};
var TESTNET_TO_MAINNET = {
  goerli: "eth",
  sepolia: "eth",
  mumbai: "polygon",
  bsc_testnet: "bsc",
  gnosis_chiado: "gnosis",
  chiliz_spicy: "chiliz",
  base_goerli: "base",
  optimism_goerli: "optimism",
  klaytn_baobab: "klaytn",
  celo_alfajores: "celo",
  kava_testnet: "kava",
  metis_goerli: "metis",
  harmony_testnet: "harmony",
  zkSync_testnet: "zkSync_era",
  aurora_testnet: "aurora",
  moonbase: "moonbeam",
  fuse_testnet: "fuse",
  linea_testnet: "linea",
  scroll_testnet: "scroll",
  holesky: "eth",
  polygon_amoy: "polygon",
  base_sepolia: "base",
  optimism_sepolia: "optimism",
  arbitrum_sepolia: "arbitrum",
  linea_sepolia: "linea",
  blast_sepolia: "blast",
  mantle_sepolia: "mantle",
  zksync_sepolia: "zkSync_era",
  polygon_zkevm_cardona: "polygon_zkevm",
  fantom_testnet: "fantom",
  lisk_sepolia: "lisk",
  flow_testnet: "flow",
  ronin_saigon: "ronin",
  zetachain_testnet: "zetachain",
  sei_testnet: "sei",
  hyperevm_testnet: "hyperevm"
};
var CHAIN_ALIASES = {
  "eth": ["ethereum", "ethereum-mainnet", "eth-mainnet"],
  "polygon": ["matic", "matic-network", "polygon-mainnet", "polygon-pos"],
  "bsc": ["binance", "binance-smart-chain", "bnb", "bnb-chain", "bnb-smart-chain"],
  "avalanche": ["avax", "avalanche-c-chain", "avax-c-chain", "avalanche-mainnet"],
  "arbitrum": ["arbitrum-one", "arb", "arbitrum-mainnet"],
  "optimism": ["op", "op-mainnet", "optimism-mainnet"],
  "base": ["base-mainnet"],
  "gnosis": ["xdai", "gnosis-chain"],
  "fantom": ["ftm", "fantom-opera"],
  "cronos": ["cro"],
  "linea": ["linea-mainnet"],
  "scroll": ["scroll-mainnet"],
  "blast": ["blast-mainnet"],
  "ronin": ["ron"],
  "sei": ["sei-mainnet"],
  "pulsechain": ["pls"],
  "polygon_zkevm": ["polygon-zkevm", "zkevm"],
  "zksync_era": ["zksync", "zksync-era", "era"],
  "mantle_sepolia": ["mantle-testnet", "mantle_testnet"],
  // Non-EVM — the short tickers callers reach for.
  "solana": ["sol"],
  "bitcoin": ["btc"],
  "polkadot": ["dot"],
  "hypercore": ["hyperliquid", "hyperliquid-core", "hyperliquid-l1"],
  "hyperevm": ["hyper-evm", "hyperliquid-evm"],
  "hypercore-testnet": ["hyperliquid-testnet", "hypercore_testnet"],
  "hyperevm_testnet": ["hyperevm-testnet", "hyperliquid-evm-testnet"],
  "monero": ["xmr"],
  "zcash": ["zec"]
};

// src/chains/registry.ts
function foldAliases(...groups) {
  const seen = /* @__PURE__ */ new Set();
  for (const group of groups) {
    for (const raw of group ?? []) {
      const alias = raw.trim().toLowerCase();
      if (alias) seen.add(alias);
    }
  }
  return [...seen];
}
var ChainRegistry = class {
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  byCaip2 = /* @__PURE__ */ new Map();
  byAlias = /* @__PURE__ */ new Map();
  register(chain, { replace = false } = {}) {
    const existingSlug = this.bySlug.get(chain.slug);
    if (existingSlug && existingSlug !== chain && !replace) {
      throw new Error(
        `Chain slug "${chain.slug}" already registered; pass replace=true to overwrite.`
      );
    }
    if (chain.chainIdHex) {
      const existingHex = this.byHex.get(chain.chainIdHex);
      if (existingHex && existingHex.slug !== chain.slug && !replace) {
        throw new Error(
          `Chain hex ${chain.chainIdHex} already mapped to slug ${existingHex.slug}; pass replace=true to overwrite.`
        );
      }
    }
    if (existingSlug) this.dropAliases(existingSlug);
    this.bySlug.set(chain.slug, chain);
    if (chain.chainIdHex) this.byHex.set(chain.chainIdHex, chain);
    if (chain.caip2) this.byCaip2.set(chain.caip2, chain);
    for (const alias of foldAliases(chain.aliases)) this.byAlias.set(alias, chain);
  }
  dropAliases(chain) {
    for (const alias of foldAliases(chain.aliases)) {
      if (this.byAlias.get(alias) === chain) this.byAlias.delete(alias);
    }
  }
  unregister(slug) {
    const chain = this.bySlug.get(slug);
    if (!chain) return null;
    this.bySlug.delete(slug);
    if (chain.chainIdHex) this.byHex.delete(chain.chainIdHex);
    if (chain.caip2) this.byCaip2.delete(chain.caip2);
    this.dropAliases(chain);
    return chain;
  }
  clear() {
    this.bySlug.clear();
    this.byHex.clear();
    this.byCaip2.clear();
    this.byAlias.clear();
  }
  get(slug) {
    return this.bySlug.get(slug) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes + lowercases). */
  getByHex(chainIdHex) {
    let normalized = chainIdHex.toLowerCase();
    if (!normalized.startsWith("0x")) normalized = "0x" + normalized.replace(/^0x/, "");
    return this.byHex.get(normalized) ?? null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"). */
  getByCaip2(caip2) {
    return this.byCaip2.get(normalizeCaip2(caip2)) ?? null;
  }
  /**
   * Resolve by CAIP-2, hex, bare decimal, slug, or alias — in that order, the
   * same order as `ChainRegistry.resolve` in `commons/chains/registry.py`, so a
   * spelling resolves to the same chain on both sides of the wire.
   */
  resolve(value) {
    if (!value) return null;
    if (value.includes(":")) {
      const hit = this.getByCaip2(value);
      if (hit) return hit;
    }
    if (value.startsWith("0x")) {
      const hit = this.getByHex(value);
      if (hit) return hit;
    }
    if (/^\d+$/.test(value)) {
      const hit = this.getByHex("0x" + BigInt(value).toString(16));
      if (hit) return hit;
    }
    const folded = value.toLowerCase();
    return this.get(folded) ?? this.byAlias.get(folded) ?? null;
  }
  byFamily(family) {
    return [...this.bySlug.values()].filter((c) => c.family === family).sort((a, b) => a.slug.localeCompare(b.slug));
  }
  all() {
    return [...this.bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
  }
};
var registry = new ChainRegistry();
function normalizeCaip2(value) {
  const split = value.trim().indexOf(":");
  if (split < 0) return value.trim();
  const namespace = value.trim().slice(0, split).toLowerCase();
  let reference = value.trim().slice(split + 1);
  if (["bip122", "polkadot"].includes(namespace)) reference = reference.toLowerCase();
  if (namespace === "eip155" && /^\d+$/.test(reference)) reference = BigInt(reference).toString();
  return `${namespace}:${reference}`;
}
function resolveChain(value) {
  return registry.resolve(value);
}
var REORG_DEPTHS_BY_SLUG = {
  eth: 12,
  polygon: 256,
  matic: 256,
  bsc: 15,
  arbitrum: 12,
  optimism: 12,
  base: 12,
  avalanche: 12,
  fantom: 12,
  cronos: 12,
  gnosis: 12,
  linea: 12,
  scroll: 12,
  mantle: 12,
  zksync: 12,
  zksync_era: 12,
  metis: 12,
  celo: 12,
  klaytn: 12,
  moonbeam: 12,
  moonriver: 12,
  harmony: 12,
  aurora: 12,
  fuse: 12,
  palm: 12,
  chiliz: 12,
  kava: 12,
  blast: 12,
  bitcoin: 6,
  solana: 32,
  // Monero's ten-block spend lock is a network rule; Zcash needs many more
  // blocks than Bitcoin for equal work at a 75-second block.
  monero: 10,
  zcash: 24,
  // HyperBFT commits a block final.
  hypercore: 1,
  hyperevm: 1,
  hyperevm_testnet: 1
};
var BLOCK_TIME_BY_SLUG = {
  eth: 12,
  etc: 13,
  polygon: 2,
  matic: 2,
  bsc: 3,
  arbitrum: 0.25,
  optimism: 2,
  base: 2,
  avalanche: 2,
  fantom: 1,
  cronos: 6,
  gnosis: 5,
  linea: 3,
  scroll: 3,
  mantle: 2,
  zksync: 1,
  zksync_era: 1,
  metis: 4,
  celo: 5,
  klaytn: 1,
  moonbeam: 12,
  moonriver: 12,
  harmony: 2,
  aurora: 1,
  fuse: 5,
  blast: 2,
  bitcoin: 600,
  solana: 0.4,
  polkadot: 6,
  monero: 120,
  zcash: 75,
  hypercore: 0.2,
  "hypercore-testnet": 0.2,
  hyperevm: 1
};
var EXPLORER_SUPPLEMENT = {
  bitcoin: "https://blockstream.info",
  solana: "https://solscan.io",
  polkadot: "https://polkadot.subscan.io",
  monero: "https://xmrchain.net",
  // Blockchair's base carries the coin as a path segment, which is exactly
  // what the link builder appends to — `/zcash` + `/transaction/<hash>`.
  zcash: "https://blockchair.com/zcash",
  // app.hyperliquid.xyz/explorer/tx/<hash>, /address/<addr>, /block/<n>.
  hypercore: "https://app.hyperliquid.xyz/explorer",
  "hypercore-testnet": "https://app.hyperliquid-testnet.xyz/explorer",
  cronos: "https://cronoscan.com",
  gnosis: "https://gnosisscan.io",
  celo: "https://celoscan.io",
  moonbeam: "https://moonscan.io",
  zkSync_era: "https://explorer.zksync.io",
  matic: "https://polygonscan.com"
};
function explorerFor(slug, rawSlug) {
  return EXPLORER_URLS[slug] ?? EXPLORER_URLS[rawSlug] ?? EXPLORER_SUPPLEMENT[slug] ?? EXPLORER_SUPPLEMENT[rawSlug] ?? null;
}
var BRAND_COLOR_BY_SLUG = {
  eth: "#627EEA",
  etc: "#328332",
  polygon: "#8247E5",
  matic: "#8247E5",
  bsc: "#F0B90B",
  arbitrum: "#28A0F0",
  optimism: "#FF0420",
  base: "#0052FF",
  avalanche: "#E84142",
  fantom: "#1969FF",
  cronos: "#002D74",
  gnosis: "#0B6E4F",
  linea: "#61DFFF",
  scroll: "#EBC28E",
  mantle: "#000000",
  zksync: "#8C8DFC",
  zksync_era: "#8C8DFC",
  metis: "#00DACC",
  celo: "#FCFF52",
  klaytn: "#FF1B1B",
  moonbeam: "#53CBC8",
  moonriver: "#C0C0C0",
  harmony: "#00AEE9",
  aurora: "#70D44B",
  fuse: "#B4F9BA",
  blast: "#FCFC03",
  kava: "#FF433E",
  bitcoin: "#F7931A",
  solana: "#14F195",
  polkadot: "#E6007A",
  monero: "#FF6600",
  zcash: "#F4B728",
  hypercore: "#97FCE4",
  hyperevm: "#97FCE4"
};
function brandColorFor(slug, mainnetSlug) {
  return BRAND_COLOR_BY_SLUG[slug] ?? (mainnetSlug ? BRAND_COLOR_BY_SLUG[mainnetSlug] ?? null : null);
}
var LOGO_URL_BY_SLUG = {};
var CAIP2_BY_SLUG = {
  "bitcoin-testnet": "bip122:000000000933ea01ad0ee984209779ba",
  "solana-devnet": "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
  "solana-testnet": "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
  bitcoin: "bip122:000000000019d6689c085ae165831e93",
  solana: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
  polkadot: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
  // Zcash is a Bitcoin fork, so `bip122` is its registered namespace and the
  // reference is its genesis hash truncated to 32 chars — Bitcoin's own rule.
  zcash: "bip122:00040fe8ec8471911baa1db1266ea15d",
  // Monero has no CASA-registered namespace; `monero` is a platform-local
  // choice. See the note on the Python side, which this table must match
  // byte for byte — `test_chains_fe_registry_parity.py` fails if it drifts.
  monero: "monero:418015bb9ae982a1975da7d79277c270",
  // HyperCore has no CASA namespace, and `hyperliquid` / `hypercore` exceed
  // CAIP-2's eight-character namespace limit. Platform-local; matches Python.
  hypercore: "hyperliq:mainnet",
  "hypercore-testnet": "hyperliq:testnet"
};
function deriveCaip2(slug, family, chainIdHex) {
  if (CAIP2_BY_SLUG[slug]) return CAIP2_BY_SLUG[slug];
  if (family === ChainFamily.EVM && chainIdHex) {
    return `eip155:${BigInt(chainIdHex).toString(10)}`;
  }
  return null;
}
var NON_EVM_OVERRIDES = {
  "bitcoin-testnet": { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin Testnet", nativeToken: "BTC", mainnetSlug: "bitcoin" },
  "solana-devnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Devnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "solana-testnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Testnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "hypercore-testnet": { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore Testnet", nativeToken: "HYPE", mainnetSlug: "hypercore" },
  bitcoin: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin", nativeToken: "BTC", aliases: ["bitcoin-mainnet", "0x0"] },
  solana: { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana", nativeToken: "SOL", aliases: ["solana-mainnet", "0x1399c"] },
  polkadot: { family: ChainFamily.OTHER, decimals: 10, chainIdHex: null, name: "Polkadot", nativeToken: "DOT", aliases: ["polkadot-mainnet", "0x162"] },
  monero: { family: ChainFamily.UTXO, decimals: 12, chainIdHex: null, name: "Monero", nativeToken: "XMR", aliases: ["monero-mainnet"] },
  zcash: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Zcash", nativeToken: "ZEC", aliases: ["zcash-mainnet"] },
  hypercore: { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore", nativeToken: "HYPE", aliases: ["hypercore-mainnet"] }
};
function layerFor(slug) {
  const folded = slug.toLowerCase();
  const listedIn = (listed) => listed.some((member) => member.toLowerCase() === folded);
  if (listedIn(LAYER1_CHAINS)) return "l1";
  if (listedIn(LAYER2_CHAINS)) return "l2";
  if (listedIn(SIDECHAINS)) return "sidechain";
  return "unknown";
}
function seedFromCanonical() {
  for (const [hexId, rawSlug] of Object.entries(CHAIN_MAPPING)) {
    const slug = rawSlug.toLowerCase();
    const config = CHAIN_CONFIG[hexId];
    const token = config?.token ?? "ETH";
    const override = NON_EVM_OVERRIDES[slug];
    const family = override?.family ?? ChainFamily.EVM;
    const decimals = override?.decimals ?? 18;
    const isTestnet = TESTNET_CHAINS.includes(rawSlug);
    let mainnetSlug = null;
    if (isTestnet) {
      const m = TESTNET_TO_MAINNET[rawSlug];
      if (m) mainnetSlug = m.toLowerCase();
    }
    const reorgDepth = REORG_DEPTHS_BY_SLUG[slug] ?? 12;
    const chainIdHex = hexId.toLowerCase();
    const metadata = { layer: layerFor(slug) };
    if (config?.gasUnit) metadata.gasUnit = config.gasUnit;
    if (slug === "etc") {
      metadata.fork_of = "eth";
      metadata.note = "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk.";
    }
    try {
      registry.register(
        {
          slug,
          chainIdHex,
          family,
          name: config?.name ?? rawSlug,
          nativeToken: token,
          decimals,
          isTestnet,
          mainnetSlug,
          reorgDepth,
          explorerUrl: explorerFor(slug, rawSlug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? (mainnetSlug ? BLOCK_TIME_BY_SLUG[mainnetSlug] ?? null : null),
          caip2: deriveCaip2(slug, family, chainIdHex),
          brandColor: brandColorFor(slug, mainnetSlug),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? (mainnetSlug ? LOGO_URL_BY_SLUG[mainnetSlug] ?? null : null),
          metadata,
          aliases: foldAliases(override?.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
  for (const [slug, ovr] of Object.entries(NON_EVM_OVERRIDES)) {
    if (registry.get(slug)) continue;
    try {
      registry.register(
        {
          slug,
          chainIdHex: ovr.chainIdHex,
          family: ovr.family,
          name: ovr.name,
          nativeToken: ovr.nativeToken,
          decimals: ovr.decimals,
          isTestnet: !!ovr.mainnetSlug,
          mainnetSlug: ovr.mainnetSlug ?? null,
          reorgDepth: REORG_DEPTHS_BY_SLUG[slug] ?? REORG_DEPTHS_BY_SLUG[ovr.mainnetSlug ?? ""] ?? 12,
          explorerUrl: explorerFor(slug, slug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? null,
          caip2: deriveCaip2(slug, ovr.family, ovr.chainIdHex),
          brandColor: brandColorFor(slug, null),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? null,
          metadata: {},
          aliases: foldAliases(ovr.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
}
seedFromCanonical();

// src/assets/cdn.ts
var CDN_KIND_DIR = {
  chain: "blockchain",
  token: "tokens",
  nft: "nfts",
  protocol: "protocols"
};
var LOGO_VARIANT_DIR = "logos";
var DEFAULT_FORMAT_BY_KIND = {
  chain: "svg",
  protocol: "svg"
};
function stemForSlug(slug) {
  const stem = slug.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  if (!stem) throw new AssetNamingError("Asset identifier has no usable characters");
  return stem;
}
function cdnChainLogoKey(input) {
  const ext = input.format ?? DEFAULT_FORMAT_BY_KIND.chain;
  const value = input.slug.trim().toLowerCase();
  const numeric = /^(0x[0-9a-f]+|[0-9]+)$/.test(value) && BigInt(value) > 0n ? `eip155:${BigInt(value)}` : value;
  const chain = resolveChain(numeric) ?? registry.all().find((entry) => entry.name.toLowerCase() === value);
  const stem = chain ? stemForSlug(chain.slug) : numeric.replace(/:/g, "_");
  if (!/^[a-z0-9][a-z0-9_.-]*$/.test(stem)) throw new AssetNamingError("Invalid chain asset identifier");
  const key = `${CDN_ASSET_ROOT}/${CDN_KIND_DIR.chain}/${LOGO_VARIANT_DIR}/${ext}/${stem}.${ext}`;
  assertValidAssetKey(key);
  return key;
}
function cdnProtocolLogoKey(input) {
  const ext = input.format ?? DEFAULT_FORMAT_BY_KIND.protocol;
  const stem = stemForSlug(input.id);
  const category = input.category ? `${stemForSlug(input.category)}/` : "";
  const key = `${CDN_ASSET_ROOT}/${CDN_KIND_DIR.protocol}/${LOGO_VARIANT_DIR}/${ext}/${category}${stem}.${ext}`;
  assertValidAssetKey(key);
  return key;
}
function cdnChainLogoUrl(slug, opts) {
  return assetUrl(cdnChainLogoKey({ slug, format: opts.format }), { cdnBase: opts.cdnBase });
}
var CCIP_SEEDED_CHAIN_SLUGS = /* @__PURE__ */ new Set([
  "arbitrum",
  "aurora_testnet",
  "avalanche",
  "base",
  "base_sepolia",
  "blast",
  "bsc",
  "bsc_testnet",
  "celo",
  "chiliz",
  "cronos",
  "eth",
  "fantom",
  "fantom_testnet",
  "flow",
  "gnosis",
  "gnosis_chiado",
  "hyperevm",
  "ink",
  "klaytn",
  "klaytn_baobab",
  "linea",
  "linea_sepolia",
  "lisk",
  "mantle",
  "mantle_sepolia",
  "metis",
  "monad",
  "moonbase",
  "moonbeam",
  "moonriver",
  "mumbai",
  "opbnb",
  "optimism",
  "plasma",
  "polygon",
  "polygon_amoy",
  "pulsechain",
  "ronin",
  "ronin_saigon",
  "scroll",
  "scroll_testnet",
  "sei",
  "sei_testnet",
  "sepolia",
  "solana",
  "solana-devnet",
  "zetachain",
  "zksync_era",
  "zksync_sepolia"
]);
function makeChainLogoResolver(opts) {
  const allow = opts.slugs ? new Set([...opts.slugs].map((s) => s.toLowerCase())) : null;
  return (_chainId, slug) => {
    if (!slug) return void 0;
    const normalized = slug.toLowerCase();
    if (allow && !allow.has(normalized)) return void 0;
    try {
      return cdnChainLogoUrl(normalized, { cdnBase: opts.cdnBase, format: opts.format });
    } catch (err) {
      if (err instanceof AssetNamingError) return void 0;
      throw err;
    }
  };
}
var defaultChainLogoResolver = null;
function configureChainLogos(opts = {}) {
  const base = opts.cdnBase?.trim();
  if (!base) {
    defaultChainLogoResolver = null;
    return;
  }
  defaultChainLogoResolver = makeChainLogoResolver({
    cdnBase: base,
    format: opts.format,
    slugs: opts.slugs === null ? void 0 : opts.slugs ?? CCIP_SEEDED_CHAIN_SLUGS
  });
}
function clearChainLogoConfig() {
  defaultChainLogoResolver = null;
}
function hasChainLogoConfig() {
  return defaultChainLogoResolver !== null;
}
function resolveChainLogoUrl(chainId, slug) {
  return defaultChainLogoResolver?.(chainId, slug);
}
async function uploadToTicket(ticket, body, opts = {}) {
  if (ticket.uploadUrl) {
    const headers = ticket.headers ?? (typeof body === "string" ? { "Content-Type": "application/json" } : void 0);
    const res = await fetch(ticket.uploadUrl, { method: "PUT", headers, body, signal: opts.signal });
    if (!res.ok) throw new Error(`[share] snapshot upload failed: HTTP ${res.status}`);
    return;
  }
  if (ticket.postUrl && ticket.fields) {
    const form = new FormData();
    for (const [k, v] of Object.entries(ticket.fields)) form.append(k, v);
    form.append("file", typeof body === "string" ? new Blob([body], { type: "application/json" }) : body);
    const res = await fetch(ticket.postUrl, { method: "POST", body: form, signal: opts.signal });
    if (!res.ok) throw new Error(`[share] snapshot upload failed: HTTP ${res.status}`);
    return;
  }
  throw new Error("[share] upload ticket has neither uploadUrl nor postUrl");
}

// src/assets/upload.ts
var ASSET_UPLOAD_TICKET_PATH = "/assets/upload-ticket";
var DEFAULT_MAX_LOGO_BYTES = 512 * 1024;
var DEFAULT_ACCEPTED_LOGO_MIME = ASSET_FORMATS.map((f) => ASSET_MIME[f]);
function validateLogoFile(file, options = {}) {
  const { maxBytes = DEFAULT_MAX_LOGO_BYTES, acceptMime = DEFAULT_ACCEPTED_LOGO_MIME } = options;
  const format = assetFormatFromExtension(file.name) ?? mimeToFormat(file.type);
  if (!format) {
    return { ok: false, error: `Unsupported file type (allowed: ${ASSET_FORMATS.join(", ")})` };
  }
  if (file.type && !acceptMime.includes(file.type)) {
    return { ok: false, error: `Unsupported MIME type "${file.type}"`, format };
  }
  if (file.size > maxBytes) {
    return { ok: false, error: `File too large (max ${Math.round(maxBytes / 1024)} KB)`, format };
  }
  if (!Number.isSafeInteger(file.size) || file.size <= 0) {
    return { ok: false, error: "File must contain image bytes", format };
  }
  return { ok: true, format };
}
function mimeToFormat(mime) {
  if (!mime) return void 0;
  return Object.keys(ASSET_MIME).find((f) => ASSET_MIME[f] === mime);
}
async function readImageDimensions(file) {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    try {
      return { width: bitmap.width, height: bitmap.height };
    } finally {
      bitmap.close();
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image dimensions"));
    };
    img.src = url;
  });
}
async function requestAssetUploadTicket(client, input, path = ASSET_UPLOAD_TICKET_PATH) {
  const res = await client.post(path, {
    kind: input.kind,
    resource_id: input.id,
    chain: input.chain,
    content_type: input.contentType,
    size_bytes: input.sizeBytes
  });
  if (!res.ok) throw new Error(`[assets] upload ticket request failed: ${res.error.message}`);
  const d = res.data ?? {};
  if (!d.key || !d.upload_key || !d.upload_url && !d.post_url) {
    throw new Error("[assets] upload ticket response missing key / upload key / upload URL");
  }
  return {
    key: d.key,
    uploadKey: d.upload_key,
    bucket: d.bucket,
    uploadUrl: d.upload_url,
    postUrl: d.post_url,
    fields: d.fields,
    headers: d.headers,
    expiresAt: d.expires_at
  };
}
async function uploadLogo(client, input, options = {}) {
  const validation = validateLogoFile(input.file, options.validate);
  if (!validation.ok) throw new Error(validation.error ?? "Invalid logo file");
  const ticket = await requestAssetUploadTicket(
    client,
    {
      kind: input.kind,
      id: input.id,
      chain: input.chain,
      contentType: input.file.type || ASSET_MIME[validation.format ?? "png"],
      sizeBytes: input.file.size,
      format: validation.format
    },
    options.path
  );
  await uploadToTicket(ticket, input.file, { signal: options.signal });
  const finalized = await client.post(options.path ?? ASSET_UPLOAD_TICKET_PATH, {
    action: "finalize",
    kind: input.kind,
    resource_id: input.id,
    chain: input.chain,
    content_type: input.file.type || ASSET_MIME[validation.format ?? "png"],
    upload_key: ticket.uploadKey
  });
  if (!finalized.ok) throw new Error(`[assets] upload validation failed: ${finalized.error.message}`);
  if (!finalized.data?.key) throw new Error("[assets] upload validation response missing key");
  return { key: finalized.data.key, cdnUrl: finalized.data.cdn_url };
}

export { ASSET_FORMATS, ASSET_MIME, ASSET_ROOT, ASSET_UPLOAD_TICKET_PATH, AssetNamingError, CCIP_SEEDED_CHAIN_SLUGS, CDN_ASSET_ROOT, CDN_KIND_DIR, DEFAULT_ACCEPTED_LOGO_MIME, DEFAULT_ASSET_FORMAT, DEFAULT_MAX_LOGO_BYTES, PRIVATE_ASSET_ROOT, addressLogoKey, assertValidAssetKey, assetFormatFromExtension, assetUrl, catalogLogoKey, cdnChainLogoKey, cdnChainLogoUrl, cdnProtocolLogoKey, chainLogoKey, clearChainLogoConfig, configureChainLogos, entityLogoKey, groupLogoKey, hasChainLogoConfig, isAssetFormat, isValidAssetSlug, labelLogoKey, makeChainLogoResolver, mimeForAssetFormat, normalizeAddressForKey, readImageDimensions, requestAssetUploadTicket, resolveChainLogoUrl, slugifyAsset, tagLogoKey, tenantLogoKey, tokenLogoKey, uploadLogo, validateLogoFile };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map